import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import OptionCard from "@/components/OptionCard";

const QuestionScreen = ({ question, answer, onAnswer, onNext, formData }) => {
  const getInitialValue = () => {
    if (answer) return answer;
    if (question.type === "multiple") return [];
    if (question.type === "multi_text") return [];
    return "";
  };

  const [localAnswer, setLocalAnswer] = useState(getInitialValue());
  const [error, setError] = useState("");

  useEffect(() => {
    setLocalAnswer(getInitialValue());
    setError("");
  }, [question.id, answer, question.type]);

  // Check if question should be shown based on conditional logic
  const shouldShow = () => {
    if (!question.conditional) return true;
    
    const { dependsOn, value } = question.conditional;
    return formData[dependsOn] === value;
  };

  // Skip if conditional not met
  useEffect(() => {
    if (!shouldShow()) {
      onNext();
    }
  }, [formData]);

  if (!shouldShow()) {
    return null;
  }

  const handleSingleSelect = (value) => {
    setLocalAnswer(value);
    setError("");
  };

  const handleMultipleSelect = (value) => {
    const currentValues = Array.isArray(localAnswer) ? localAnswer : [];
    const EXCLUSIVE_OPTION = "ninguna"; // "Ninguna con claridad" option value
    
    // If clicking on an already selected option, just remove it
    if (currentValues.includes(value)) {
      setLocalAnswer(currentValues.filter(v => v !== value));
    } else {
      // If selecting "Ninguna con claridad", clear all other selections
      if (value === EXCLUSIVE_OPTION) {
        setLocalAnswer([EXCLUSIVE_OPTION]);
      } 
      // If selecting any other option while "Ninguna con claridad" is selected, remove it
      else if (currentValues.includes(EXCLUSIVE_OPTION)) {
        setLocalAnswer([value]);
      }
      // Normal case: add to selection
      else {
        setLocalAnswer([...currentValues, value]);
      }
    }
    setError("");
  };

  const handleTextChange = (value) => {
    setLocalAnswer(value);
    setError("");
  };

  const handleContinue = () => {
    // Validation
    if (question.type === "multiple") {
      if (!Array.isArray(localAnswer) || localAnswer.length === 0) {
        setError("Por favor, selecciona al menos una opción.");
        return;
      }
    } else if (question.type === "multi_text") {
      // Filter out empty strings and check if at least one has content
      const filledValues = Array.isArray(localAnswer) 
        ? localAnswer.filter(v => v && v.trim() !== "") 
        : [];
      if (filledValues.length === 0) {
        setError("Por favor, introduce al menos un proceso.");
        return;
      }
      // Save only non-empty values
      onAnswer(question.id, filledValues);
      onNext();
      return;
    } else if (!localAnswer || (typeof localAnswer === "string" && localAnswer.trim() === "")) {
      setError("Por favor, completa este campo.");
      return;
    }

    onAnswer(question.id, localAnswer);
    onNext();
  };

  const handleBack = () => {
    if (question.onBack) {
      question.onBack();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pb-8 sm:pb-12 px-4 slide-up" style={{ paddingTop: '110px' }}>
      <div className="max-w-2xl w-full space-y-6 sm:space-y-8 md:space-y-10">
        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[hsl(var(--text-primary))] leading-tight" style={{ letterSpacing: '-0.02em' }}>
            {question.question}
          </h2>
          
          {question.microcopy && (
            <p className="text-sm sm:text-base text-[hsl(var(--text-secondary))]" style={{ letterSpacing: '-0.01em' }}>
              {question.microcopy}
            </p>
          )}
        </div>

        <div className="space-y-3 sm:space-y-4">
          {/* Single choice - Card buttons */}
          {question.type === "single" && question.options && (
            <div className="grid gap-3">
              {question.options.map((option) => (
                <OptionCard
                  key={option.value}
                  label={option.label}
                  selected={localAnswer === option.value}
                  onClick={() => handleSingleSelect(option.value)}
                />
              ))}
            </div>
          )}

          {/* Multiple choice - Checkboxes in cards */}
          {question.type === "multiple" && question.options && (
            <div className="grid gap-4">
              {question.options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleMultipleSelect(option.value)}
                  className="bg-[hsl(var(--bg-card))] border border-[hsl(var(--border-light))] rounded-[14px] p-5 cursor-pointer flex items-start gap-3 transition-all hover:border-[hsl(var(--accent-primary))] hover:-translate-y-0.5 hover:shadow-[0_12px_24px_-6px_rgba(0,0,0,0.12)]"
                  style={{
                    transitionTimingFunction: 'cubic-bezier(0.2, 0.8, 0.2, 1)'
                  }}
                >
                  <Checkbox
                    checked={Array.isArray(localAnswer) && localAnswer.includes(option.value)}
                    onCheckedChange={() => handleMultipleSelect(option.value)}
                    className="mt-0.5"
                  />
                  <label className="text-[15px] font-medium text-[hsl(var(--text-primary))] cursor-pointer flex-1 leading-snug">
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          )}

          {/* Text input */}
          {question.type === "text" && (
            <Input
              value={localAnswer}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder={question.placeholder || "Escribe tu respuesta..."}
              className="w-full h-12 text-base"
            />
          )}

          {/* Textarea */}
          {question.type === "textarea" && (
            <Textarea
              value={localAnswer}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder={question.placeholder || "Escribe tu respuesta..."}
              className="w-full min-h-[200px] text-base resize-none"
            />
          )}

          {/* Multi Text - Multiple text inputs */}
          {question.type === "multi_text" && (
            <div className="grid gap-3">
              {Array.from({ length: question.maxInputs || 3 }).map((_, index) => (
                <Input
                  key={index}
                  value={Array.isArray(localAnswer) ? localAnswer[index] || "" : ""}
                  onChange={(e) => {
                    const newValues = Array.isArray(localAnswer) ? [...localAnswer] : [];
                    newValues[index] = e.target.value;
                    setLocalAnswer(newValues);
                    setError("");
                  }}
                  placeholder={question.placeholders?.[index] || `Proceso ${index + 1}`}
                  className="w-full h-12 text-base"
                />
              ))}
            </div>
          )}
        </div>

        {error && (
          <p className="text-sm text-[hsl(var(--state-error))]">{error}</p>
        )}

        <div className="pt-4">
          <div className="flex items-center gap-4">
            {/* Botón Atrás */}
            {question.onBack && (
              <Button
                onClick={handleBack}
                variant="secondary"
                size="lg"
                className="h-14 px-6 text-base font-semibold"
              >
                Atrás
              </Button>
            )}
            
            {/* Botón Continuar */}
            <Button
              onClick={handleContinue}
              size="lg"
              className="flex-1 h-14 text-base font-semibold"
            >
              Continuar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionScreen;
